import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Download,
  UserX,
  UserCheck,
  Eye,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import type { Database } from '../types/database.types';
import Confirm from '../components/ui/Confirm';
import EliminarAlumnoModal from '../components/EliminarAlumnoModal';
import RevisarCategoriasModal from '../components/RevisarCategorias';
import { correoDeContacto, loadFamilies, type FamilyOf } from '../services/guardians';

// Shape devuelto por `loadAlumnos`. El cliente sin <Database> infiere los
// embeds como arrays — doble cast vía `unknown` (patrón ADR-0020).
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type EnrollmentRow = Database['public']['Tables']['enrollments']['Row'];
type UserCreditRow = Database['public']['Tables']['user_credits']['Row'];

type EnrollmentEmbed = Pick<EnrollmentRow, 'status'> & {
  sessions: { groups: { name: string } | null } | null;
};
type CreditEmbed = Pick<
  UserCreditRow,
  'sessions_remaining' | 'active' | 'expiry_date' | 'credit_type'
>;

type ProfileWithEnrollAndCredits = Pick<
  ProfileRow,
  | 'id'
  | 'full_name'
  | 'email'
  | 'phone'
  | 'position'
  | 'is_minor'
  | 'active'
  | 'created_at'
  | 'sede_id'
  | 'club'
  | 'team'
  | 'division'
  | 'membership_active_until'
> & {
  enrollments: EnrollmentEmbed[];
  user_credits: CreditEmbed[];
};

interface Alumno {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  is_minor: boolean;
  active: boolean;
  created_at: string;
  sede_id: string | null;
  club?: string | null;
  team?: string | null;
  division?: string | null;
  membership_active_until: string | null;
  grupo?: string;
  sesiones_grupales?: number;
  sesiones_individuales?: number;
  sesiones_especiales?: number;
  tiene_gym?: boolean;
}

const POSITION_LABELS: Record<string, string> = {
  portero: 'Portero',
  defensa: 'Defensa',
  centrocampista: 'Centrocampista',
  delantero: 'Delantero',
  extremo: 'Extremo',
};

// Matrícula vigente = fecha futura. NULL o fecha pasada → sin matrícula activa.
function isMembershipActive(until: string | null): boolean {
  return until != null && new Date(until).getTime() > Date.now();
}

export default function AlumnosPage() {
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  // Activar/desactivar es una acción de fila: no tiene modal donde contar un
  // rechazo de la base. El comentario de `alumnoBorrado` explica lo que pasa
  // cuando el estado local se cambia sin mirar el resultado.
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'inactivos'>('activos');
  const [filtroMatricula, setFiltroMatricula] = useState<'todas' | 'con' | 'sin'>('todas');
  const [sortMatricula, setSortMatricula] = useState<'none' | 'asc' | 'desc'>('none');
  const [confirmDesactivar, setConfirmDesactivar] = useState<Alumno | null>(null);
  const [confirmEliminar, setConfirmEliminar] = useState<Alumno | null>(null);
  // [343] LA RE-DERIVACIÓN DE CATEGORÍAS. Es un botón y no un cron: enseña a
  // quién cambiaría y lo aplica una persona. El modal vive en
  // `components/RevisarCategorias` porque la ficha del alumno abre el mismo:
  // dos copias serían dos puertas que escriben categorías.
  const [rederivOpen, setRederivOpen] = useState(false);

  // ⚠️ Aquí había una suscripción realtime a `profiles` que llamaba a
  // `loadAlumnos()` en cada cambio de la tabla. **Nunca recibió un evento**, ni
  // en producción: `profiles` no está en la publicación `supabase_realtime`, así
  // que el canal se abría, se suscribía y no llegaba nada. Nadie lo notó porque
  // recargar la página funciona. Es la misma clase de fallo que la mig 310 cerró
  // para `messages`: código escrito dando por hecho que la tabla estaba
  // publicada.
  //
  // Se borra en vez de publicar la tabla (ver la mig 321): un listado de
  // dirección que se refresca al entrar es suficiente, nadie ha echado de menos
  // ese refresco en meses, y publicar `profiles` sería ampliar superficie con
  // datos personales para arreglar algo que a nadie le falta. El guard 5 de la
  // 321 impide que entre en la publicación sin que se decida a propósito.
  useEffect(() => {
    loadAlumnos();
  }, []);

  const [families, setFamilies] = useState<Map<string, FamilyOf>>(new Map());
  // La columna Familia pinta «—» cuando un alumno no tiene tutor. Con la
  // lectura caída el mapa quedaba vacío y TODAS las filas decían «—»: el
  // listado afirmaba que ningún alumno del club tiene familia vinculada.
  const [familiasError, setFamiliasError] = useState<string | null>(null);
  // «No se encontraron alumnos» es la frase de un filtro sin resultados, y con
  // la lectura caída decía lo mismo: el club entero sin alumnos, leído como
  // «prueba con otro texto en el buscador».
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  async function loadAlumnos() {
    setLoading(true);
    // La familia se resuelve de una vez para todo el listado: alumno por alumno
    // serían N+1 viajes para pintar una columna.
    void loadFamilies().then(({ familias, error }) => {
      setFamiliasError(error);
      setFamilies(familias);
    });
    const { data: profiles, error: errorLectura } = await supabase
      .from('profiles')
      .select(
        `
        id, full_name, email, phone, position, is_minor, active, created_at, sede_id,
        club, team, division, membership_active_until,
        enrollments!enrollments_user_id_fkey(status, sessions(groups(name))),
        user_credits!user_id(sessions_remaining, active, expiry_date, credit_type)
      `
      )
      .eq('role', 'alumno')
      // Los tombstones RGPD (mig 184) siguen en `profiles` como ancla de lo
      // que hay obligación de conservar, pero no son alumnos gestionables:
      // sin este filtro el listado se llenaría de "Usuario eliminado".
      .is('deleted_at', null)
      .order('full_name');

    // Dos embeds anidados (`enrollments → sessions → groups`) y uno por FK
    // nombrada (`user_credits!user_id`, que tiene dos hacia `profiles`): sitio
    // de sobra para el 300 PGRST201 del #496.
    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setAlumnos([]);
      setLoading(false);
      return;
    }
    setErrorCarga(null);

    const rows = (profiles ?? []) as unknown as ProfileWithEnrollAndCredits[];
    const mapped: Alumno[] = rows.map((p) => {
      const grupoActivo = p.enrollments?.find((e) => e.status === 'confirmed')?.sessions?.groups
        ?.name;
      const creditosActivos = (p.user_credits ?? []).filter(
        (c) => c.active && (!c.expiry_date || new Date(c.expiry_date) > new Date())
      );
      const sumByType = (type: string) =>
        creditosActivos
          .filter((c) => c.credit_type === type)
          .reduce((sum, c) => sum + (c.sessions_remaining ?? 0), 0);
      return {
        id: p.id,
        full_name: p.full_name,
        email: p.email,
        phone: p.phone,
        position: p.position,
        is_minor: p.is_minor,
        active: p.active,
        created_at: p.created_at,
        sede_id: p.sede_id,
        club: p.club,
        team: p.team,
        division: p.division,
        membership_active_until: p.membership_active_until,
        grupo: grupoActivo ?? '—',
        sesiones_grupales: sumByType('grupal'),
        sesiones_individuales: sumByType('individual'),
        sesiones_especiales: sumByType('especial'),
        tiene_gym: creditosActivos.some((c) => c.credit_type === 'gym'),
      };
    });

    setAlumnos(mapped);
    setLoading(false);
  }

  const alumnosFiltrados = useMemo(() => {
    const filtrados = alumnos.filter((a) => {
      // Se busca por el correo QUE SE VE. Dirección teclea el del tutor,
      // que desde la 297 es el único que existe para 32 alumnos; buscar solo
      // por `a.email` dejaba a esas familias sin encontrar, y el marcador
      // técnico no lo teclea nadie.
      const contacto = correoDeContacto(a.email, families.get(a.id))?.email ?? '';
      const q = search.toLowerCase();
      const matchSearch =
        a.full_name.toLowerCase().includes(q) ||
        contacto.toLowerCase().includes(q) ||
        (a.club ?? '').toLowerCase().includes(q);
      const matchFiltro =
        filtroActivo === 'todos' ? true : filtroActivo === 'activos' ? a.active : !a.active;
      const activa = isMembershipActive(a.membership_active_until);
      const matchMatricula =
        filtroMatricula === 'todas' ? true : filtroMatricula === 'con' ? activa : !activa;
      return matchSearch && matchFiltro && matchMatricula;
    });

    if (sortMatricula === 'none') return filtrados;
    // Orden por fecha de fin de matrícula; "sin matrícula" (null) siempre al final.
    return [...filtrados].sort((a, b) => {
      const av = a.membership_active_until ? new Date(a.membership_active_until).getTime() : null;
      const bv = b.membership_active_until ? new Date(b.membership_active_until).getTime() : null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortMatricula === 'asc' ? av - bv : bv - av;
    });
  }, [alumnos, families, search, filtroActivo, filtroMatricula, sortMatricula]);

  async function toggleActivo(alumno: Alumno) {
    const { error } = await supabase
      .from('profiles')
      .update({ active: !alumno.active })
      .eq('id', alumno.id);
    // El error se comprueba ANTES de tocar el estado: pintar la fila con el
    // valor nuevo cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      setListError(
        `No se pudo ${alumno.active ? 'desactivar' : 'activar'} a ${alumno.full_name}: ${error.message}`
      );
      return;
    }
    setListError('');
    setAlumnos((prev) => prev.map((a) => (a.id === alumno.id ? { ...a, active: !a.active } : a)));
  }

  // El borrado vive en EliminarAlumnoModal y pasa por la RPC `admin_delete_account`.
  //
  // Aquí había un `supabase.from('profiles').delete()` a pelo cuyo error se
  // descartaba, seguido de un filter del estado local: la fila desaparecía de
  // la pantalla SIEMPRE, incluso cuando el borrado había abortado. Así estuvo
  // una cuenta un mes viva después de darla por borrada.
  //
  // Ahora la fila solo se quita cuando el servidor confirma el recuento.
  function alumnoBorrado(id: string) {
    setAlumnos((prev) => prev.filter((a) => a.id !== id));
  }

  function exportCSV() {
    const header =
      'Nombre,Email,Teléfono,Posición,Grupo,Sesiones Grupales,Sesiones Individuales,Sesiones Especiales,PF,Matrícula hasta,Activo,Fecha Registro';
    const rows = alumnosFiltrados.map(
      (a) =>
        `"${a.full_name}","${correoDeContacto(a.email, families.get(a.id))?.email ?? ''}","${a.phone ?? ''}","${POSITION_LABELS[a.position ?? ''] ?? ''}","${a.grupo}","${a.sesiones_grupales ?? 0}","${a.sesiones_individuales ?? 0}","${a.sesiones_especiales ?? 0}","${a.tiene_gym ? 'Sí' : 'No'}","${isMembershipActive(a.membership_active_until) ? format(new Date(a.membership_active_until as string), 'dd/MM/yyyy') : 'Sin matrícula'}","${a.active ? 'Sí' : 'No'}","${format(new Date(a.created_at), 'dd/MM/yyyy')}"`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alumnos-vertex-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="alumnos-list-error"
        >
          {listError}
        </p>
      )}

      {/* Barra de acciones */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {(['activos', 'inactivos', 'todos'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFiltroActivo(f)}
              className={
                filtroActivo === f
                  ? 'btn-gold text-xs px-3 py-1.5'
                  : 'btn-ghost text-xs px-3 py-1.5'
              }
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <span className="w-px bg-bg-border mx-1" />
          {(
            [
              ['todas', 'Matrícula: todas'],
              ['con', 'Con matrícula'],
              ['sin', 'Sin matrícula'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              data-testid={`filtro-matricula-${value}`}
              onClick={() => setFiltroMatricula(value)}
              className={
                filtroMatricula === value
                  ? 'btn-gold text-xs px-3 py-1.5'
                  : 'btn-ghost text-xs px-3 py-1.5'
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              className="input pl-9 h-9 text-sm"
              placeholder="Buscar por nombre, email o club..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button onClick={exportCSV} className="btn-ghost flex items-center gap-2 h-9 px-3">
            <Download size={16} />
            <span className="hidden sm:inline text-sm">CSV</span>
          </button>
          <button
            onClick={() => setRederivOpen(true)}
            data-testid="btn-rederivar"
            className="btn-ghost flex items-center gap-2 h-9 px-3"
            title="Revisar las categorías contra el año de nacimiento"
          >
            <span className="text-sm">Revisar categorías</span>
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Contacto</th>
                  <th className="table-th">Posición</th>
                  <th className="table-th">Familia</th>
                  <th className="table-th text-center">Grupal</th>
                  <th className="table-th text-center">Individual</th>
                  <th className="table-th text-center">Especial</th>
                  <th className="table-th text-center">PF</th>
                  <th className="table-th">
                    <button
                      type="button"
                      data-testid="sort-matricula"
                      onClick={() =>
                        setSortMatricula((s) =>
                          s === 'none' ? 'desc' : s === 'desc' ? 'asc' : 'none'
                        )
                      }
                      className="flex items-center gap-1 hover:text-gold-primary transition-colors"
                      title="Ordenar por fin de matrícula"
                    >
                      Matrícula
                      {sortMatricula === 'none' ? (
                        <ArrowUpDown size={12} />
                      ) : sortMatricula === 'desc' ? (
                        <ArrowDown size={12} />
                      ) : (
                        <ArrowUp size={12} />
                      )}
                    </button>
                  </th>
                  <th className="table-th">Estado</th>
                  <th className="table-th">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {errorCarga ? (
                  <tr>
                    <td colSpan={11} className="table-td text-center py-12">
                      <span className="text-error text-sm" data-testid="alumnos-ilegibles">
                        No se ha podido cargar el listado de alumnos. No pruebe otro filtro: la
                        consulta ha fallado.
                      </span>
                      <button
                        className="btn-secondary block mx-auto mt-3"
                        onClick={loadAlumnos}
                        data-testid="alumnos-reintentar"
                      >
                        Reintentar
                      </button>
                    </td>
                  </tr>
                ) : alumnosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="table-td text-center text-text-secondary py-12">
                      No se encontraron alumnos
                    </td>
                  </tr>
                ) : (
                  alumnosFiltrados.map((alumno) => (
                    <tr
                      key={alumno.id}
                      className="table-row"
                      data-testid={`alumno-row-${alumno.id}`}
                    >
                      <td className="table-td">
                        <div>
                          <p className="font-medium">{alumno.full_name}</p>
                          {alumno.is_minor && (
                            <span className="badge-warning text-xs mt-0.5">Menor</span>
                          )}
                        </div>
                      </td>
                      <td className="table-td" data-testid={`contacto-${alumno.id}`}>
                        {(() => {
                          // El correo del TUTOR cuando lo hay. Antes se pintaba
                          // `alumno.email` a pelo, y desde la 297 eso es el
                          // marcador técnico `<uuid>@no-login.…` en 32 alumnos:
                          // una columna de contacto que no servía para contactar.
                          const c = correoDeContacto(alumno.email, families.get(alumno.id));
                          if (!c) {
                            return (
                              <p
                                className="text-text-secondary text-xs"
                                title="Ni el alumno ni su tutor tienen un correo al que escribir"
                                data-testid={`sin-contacto-${alumno.id}`}
                              >
                                sin correo
                              </p>
                            );
                          }
                          return (
                            <p className="text-text-secondary text-xs">
                              {c.email}
                              {c.esDelTutor && (
                                <span
                                  className="badge-info text-xs ml-1"
                                  title="Es el correo del tutor, no el del alumno"
                                >
                                  tutor
                                </span>
                              )}
                            </p>
                          );
                        })()}
                        <p className="text-text-secondary text-xs">{alumno.phone ?? '—'}</p>
                      </td>
                      <td className="table-td text-text-secondary">
                        {POSITION_LABELS[alumno.position ?? ''] ?? '—'}
                      </td>
                      <td className="table-td" data-testid={`familia-${alumno.id}`}>
                        {(() => {
                          // «—» significa «no tiene tutor vinculado»; «?»,
                          // «no se ha podido leer». Son cosas distintas y la
                          // columna las distingue.
                          if (familiasError) {
                            return (
                              <span
                                className="text-error text-xs"
                                title={`No se ha podido leer la familia: ${familiasError}`}
                                data-testid={`familia-ilegible-${alumno.id}`}
                              >
                                ?
                              </span>
                            );
                          }
                          const fam = families.get(alumno.id);
                          if (!fam || fam.guardians.length === 0) {
                            return <span className="text-text-secondary text-xs">—</span>;
                          }
                          return (
                            <div>
                              <p className="text-xs">
                                {fam.guardians.map((g) => g.name).join(', ')}
                              </p>
                              {fam.siblings.length > 0 && (
                                <span
                                  className="badge-info text-xs mt-0.5"
                                  data-testid={`hermanos-${alumno.id}`}
                                >
                                  {fam.siblings.length === 1
                                    ? '1 hermano'
                                    : `${fam.siblings.length} hermanos`}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="table-td text-center">
                        <span
                          className={`font-oswald text-lg ${(alumno.sesiones_grupales ?? 0) <= 2 ? 'text-error' : 'text-gold-primary'}`}
                        >
                          {alumno.sesiones_grupales ?? 0}
                        </span>
                      </td>
                      <td className="table-td text-center">
                        <span
                          className={`font-oswald text-lg ${(alumno.sesiones_individuales ?? 0) === 0 ? 'text-text-secondary' : 'text-gold-primary'}`}
                        >
                          {alumno.sesiones_individuales ?? 0}
                        </span>
                      </td>
                      <td className="table-td text-center">
                        <span
                          className={`font-oswald text-lg ${(alumno.sesiones_especiales ?? 0) === 0 ? 'text-text-secondary' : 'text-gold-primary'}`}
                        >
                          {alumno.sesiones_especiales ?? 0}
                        </span>
                      </td>
                      <td className="table-td text-center">
                        {alumno.tiene_gym ? (
                          <span className="badge-success">SÍ</span>
                        ) : (
                          <span className="text-text-secondary text-xs">NO</span>
                        )}
                      </td>
                      <td className="table-td" data-testid={`matricula-cell-${alumno.id}`}>
                        {isMembershipActive(alumno.membership_active_until) ? (
                          <span className="text-success text-xs font-medium">
                            {format(
                              new Date(alumno.membership_active_until as string),
                              'dd/MM/yyyy'
                            )}
                          </span>
                        ) : (
                          <span className="text-text-secondary text-xs">Sin matrícula</span>
                        )}
                      </td>
                      <td className="table-td">
                        <span className={alumno.active ? 'badge-success' : 'badge-error'}>
                          {alumno.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="table-td">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/alumnos/${alumno.id}`}
                            className="text-text-secondary hover:text-gold-primary transition-colors"
                            title="Ver ficha"
                            data-testid={`alumno-link-${alumno.id}`}
                          >
                            <Eye size={16} />
                          </Link>
                          <button
                            onClick={() => setConfirmDesactivar(alumno)}
                            className="text-text-secondary hover:text-error transition-colors"
                            title={alumno.active ? 'Desactivar' : 'Activar'}
                          >
                            {alumno.active ? <UserX size={16} /> : <UserCheck size={16} />}
                          </button>
                          {!alumno.active && (
                            <button
                              onClick={() => setConfirmEliminar(alumno)}
                              className="text-text-secondary hover:text-error transition-colors"
                              title="Eliminar definitivamente"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-text-secondary text-xs font-inter">
        {alumnosFiltrados.length} alumno{alumnosFiltrados.length !== 1 ? 's' : ''} mostrado
        {alumnosFiltrados.length !== 1 ? 's' : ''}
      </p>

      <RevisarCategoriasModal
        open={rederivOpen}
        onClose={() => setRederivOpen(false)}
        onAplicado={loadAlumnos}
      />

      <Confirm
        open={!!confirmDesactivar}
        onClose={() => setConfirmDesactivar(null)}
        onConfirm={() => confirmDesactivar && toggleActivo(confirmDesactivar)}
        title={confirmDesactivar?.active ? 'Desactivar alumno' : 'Activar alumno'}
        message={`¿Seguro que quieres ${confirmDesactivar?.active ? 'desactivar' : 'activar'} a ${confirmDesactivar?.full_name}?`}
        confirmLabel={confirmDesactivar?.active ? 'Desactivar' : 'Activar'}
        danger={confirmDesactivar?.active}
      />

      <EliminarAlumnoModal
        open={!!confirmEliminar}
        cuenta={confirmEliminar}
        onClose={() => setConfirmEliminar(null)}
        onDeleted={alumnoBorrado}
      />
    </div>
  );
}
